import { useMutation, useQuery } from '@tanstack/react-query';
import { FileText, Loader2, Sparkles } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { HelpHint } from '../components/HelpHint';
import { PageNav } from '../components/PageNav';
import { api } from '../lib/api';

export function NewTicketPage() {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    title: '',
    description: '',
    jobTypeId: '',
    priority: '',
    areaId: '',
    customerId: '',
    siteId: '',
  });
  const [areaQuery, setAreaQuery] = useState('');

  /**
   * What the ticket still needs, in the words used on the form.
   *
   * Both buttons below used to be `disabled` until these were filled, which is
   * the worst screen in the application to be silent on: somebody is on the
   * telephone to a customer, they have typed what they can, and the only
   * button that matters does nothing when pressed and gives no reason.
   *
   * The same shape was reported on the stock list — "the submit button is not
   * adding anything" — and the fix is the same. The buttons stay pressable and
   * answer, and the answer is also on screen before anybody presses anything.
   */
  const missing = [
    form.title.trim() ? null : 'a short title',
    form.jobTypeId ? null : 'a job type',
  ].filter(Boolean) as string[];

  const [notice, setNotice] = useState<string | null>(null);

  /** True if it went ahead; false if it stopped and said what was missing. */
  const tryToCreate = (asDraft: boolean) => {
    if (missing.length > 0) {
      setNotice(`This needs ${missing.join(' and ')} before it can be saved.`);
      return;
    }
    setNotice(null);
    create.mutate(asDraft);
  };
  const [report, setReport] = useState('');
  const [aiRunId, setAiRunId] = useState<string | null>(null);
  const [aiNotes, setAiNotes] = useState<{ missing: string[]; safety: string[]; why: string } | null>(
    null,
  );

  // AI.md §2.1 — it fills the form in, a human confirms it. Nothing is created
  // until the dispatcher presses the same button they always press.
  const suggest = useMutation({
    mutationFn: () => api.aiIntake(report),
    onSuccess: (response) => {
      if (!response.available || !response.suggestion) {
        setAiNotes({ missing: [], safety: [], why: 'AI is not available — fill the form in as usual.' });
        return;
      }
      const s = response.suggestion;
      setAiRunId(response.runId ?? null);
      setForm((current) => ({
        ...current,
        title: s.title || current.title,
        description: s.description || current.description,
        jobTypeId: s.jobTypeId ?? current.jobTypeId,
        priority: s.suggestedPriority || current.priority,
        customerId: s.customerId ?? current.customerId,
      }));
      if (s.areaName) setAreaQuery(s.areaName);
      setAiNotes({ missing: s.missingInfo, safety: s.safetyFlags, why: s.priorityReason });
    },
  });

  const jobTypes = useQuery({ queryKey: ['job-types'], queryFn: api.jobTypes });
  const areas = useQuery({ queryKey: ['areas'], queryFn: () => api.areas() });
  const customers = useQuery({ queryKey: ['customers'], queryFn: api.customers });

  const create = useMutation({
    mutationFn: (asDraft: boolean) =>
      api.createTicket({
        title: form.title,
        description: form.description || undefined,
        jobTypeId: form.jobTypeId,
        priority: form.priority || undefined,
        areaId: form.areaId || undefined,
        customerId: form.customerId || undefined,
        siteId: form.siteId || undefined,
        asDraft,
      }),
    onSuccess: async (created) => {
      // Whether the dispatcher kept the suggestion is the only metric that
      // reflects real value (AI.md §5).
      if (aiRunId) await api.aiFeedback(aiRunId, true).catch(() => undefined);
      navigate(`/tickets/${created.id}`);
    },
  });

  // Typed, not browsed — even 40 areas is too many to scroll on a phone.
  const matchingAreas = useMemo(() => {
    const all = areas.data?.areas ?? [];
    if (!areaQuery) return all.slice(0, 8);
    const needle = areaQuery.toLowerCase();
    return all.filter((a) => a.name.toLowerCase().includes(needle)).slice(0, 8);
  }, [areas.data, areaQuery]);

  const selectedArea = areas.data?.areas.find((a) => a.id === form.areaId);
  const selectedCustomer = customers.data?.customers.find((c) => c.id === form.customerId);

  return (
    <div className="mx-auto max-w-2xl space-y-5">
      <PageNav
        backTo="/list"
        backLabel="Back to tickets"
        crumbs={[{ label: 'Tickets', to: '/list' }, { label: 'New ticket' }]}
      />

      <h2 className="font-heading text-2xl">New ticket</h2>

      <form
        className="card space-y-5"
        onSubmit={(event) => {
          event.preventDefault();
          tryToCreate(false);
        }}
      >
        <div className="rounded-xl border border-primary-200 bg-primary-50/40 p-3">
          <label htmlFor="report" className="label">
            <Sparkles className="h-4 w-4 text-primary-500" aria-hidden="true" />
            Paste what the client said
            <HelpHint>
              Optional. It fills the form in for you to check and correct — nothing is created until
              you press Create ticket.
            </HelpHint>
          </label>
          <textarea
            id="report"
            rows={2}
            value={report}
            onChange={(e) => setReport(e.target.value)}
            placeholder="e.g. Kemunto from the salon in Jogoo says there is no internet since morning"
            className="input-field"
          />
          <button
            type="button"
            onClick={() => suggest.mutate()}
            disabled={report.trim().length < 5 || suggest.isPending}
            className="btn-secondary mt-2"
          >
            {suggest.isPending && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
            Fill the form for me
          </button>
          {report.trim().length < 5 && (
            <span className="ml-2 text-xs text-muted-foreground">
              Paste a sentence above first.
            </span>
          )}

          {aiNotes && (
            <div className="mt-3 space-y-2 text-sm">
              {aiNotes.why && <p className="text-muted-foreground">Priority: {aiNotes.why}</p>}
              {aiNotes.missing.length > 0 && (
                <div>
                  <p className="font-semibold">Ask before sending anyone:</p>
                  <ul className="list-inside list-disc text-muted-foreground">
                    {aiNotes.missing.map((q) => (
                      <li key={q}>{q}</li>
                    ))}
                  </ul>
                </div>
              )}
              {aiNotes.safety.length > 0 && (
                <p className="rounded-lg bg-accent-50 p-2 text-accent-600">
                  {aiNotes.safety.join(' · ')}
                </p>
              )}
              <p className="text-xs text-muted-foreground">
                A suggestion, not a decision. Check every field before creating.
              </p>
            </div>
          )}
        </div>

        <div>
          <label htmlFor="jobType" className="label">
            Job type
            <HelpHint>
              Determines the required skills, the SLA target and the form the technician fills in on
              site. Pick the closest match — it can be changed before dispatch.
            </HelpHint>
          </label>
          <select
            id="jobType"
            required
            value={form.jobTypeId}
            onChange={(e) => setForm({ ...form, jobTypeId: e.target.value })}
            className="input-field"
          >
            <option value="">Choose…</option>
            {jobTypes.data?.jobTypes.map((type) => (
              <option key={type.id} value={type.id}>
                {type.name} ({type.category})
              </option>
            ))}
          </select>
        </div>

        <div>
          <label htmlFor="title" className="label">
            What did the client say?
            <HelpHint>
              The client&apos;s own words. Your diagnosis goes in the notes — this field is the
              report.
            </HelpHint>
          </label>
          <input
            id="title"
            required
            minLength={3}
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            placeholder="No internet since this morning"
            className="input-field"
          />
        </div>

        <div>
          <label htmlFor="priority" className="label">
            Priority
            <HelpHint>
              P1 = backbone or POP down, or 10+ clients affected. P2 = business client. P3 = single
              home client. P4 = slow speeds, WiFi, surveys.
            </HelpHint>
          </label>
          <select
            id="priority"
            value={form.priority}
            onChange={(e) => setForm({ ...form, priority: e.target.value })}
            className="input-field"
          >
            <option value="">Use the job type default</option>
            <option value="P1">P1 — backbone / 10+ clients</option>
            <option value="P2">P2 — business client</option>
            <option value="P3">P3 — single home client</option>
            <option value="P4">P4 — degraded / survey</option>
          </select>
        </div>

        <div>
          <label htmlFor="area" className="label">
            Area
            <HelpHint>
              Where in Kisii the job is. This sets the zone and filters which technicians are
              suggested — you can still assign anyone.
            </HelpHint>
          </label>
          <input
            id="area"
            value={areaQuery}
            onChange={(e) => {
              setAreaQuery(e.target.value);
              setForm({ ...form, areaId: '' });
            }}
            placeholder="Start typing — Suneka, Ogembo, Keroka…"
            className="input-field"
            autoComplete="off"
          />

          {selectedArea ? (
            <p className="mt-2 text-sm">
              <strong>{selectedArea.name}</strong> · zone {selectedArea.zoneCode} ·{' '}
              <span className="text-muted-foreground">~{selectedArea.travelMin} min from town</span>
            </p>
          ) : (
            <ul className="mt-2 flex flex-wrap gap-1.5">
              {matchingAreas.map((area) => (
                <li key={area.id}>
                  <button
                    type="button"
                    onClick={() => {
                      setForm({ ...form, areaId: area.id });
                      setAreaQuery(area.name);
                    }}
                    className="rounded-lg border border-border px-2.5 py-1 text-xs hover:bg-muted"
                  >
                    {area.name}
                    <span className="ml-1 text-muted-foreground">{area.zoneCode}</span>
                  </button>
                </li>
              ))}
              {areaQuery && matchingAreas.length === 0 && (
                <li className="text-sm text-muted-foreground">
                  No area matches “{areaQuery}”. Check the spelling, or add it in Settings.
                </li>
              )}
            </ul>
          )}
        </div>

        <div>
          <label htmlFor="customer" className="label">
            Customer
            <HelpHint>
              Start typing a name. Their sites load automatically, with the house number and landmark
              the technician will need.
            </HelpHint>
          </label>
          <select
            id="customer"
            value={form.customerId}
            onChange={(e) => setForm({ ...form, customerId: e.target.value, siteId: '' })}
            className="input-field"
          >
            <option value="">None (network or internal job)</option>
            {customers.data?.customers.map((customer) => (
              <option key={customer.id} value={customer.id}>
                {customer.name}
              </option>
            ))}
          </select>
        </div>

        {selectedCustomer && selectedCustomer.sites.length > 0 && (
          <div>
            <label htmlFor="site" className="label">
              Site
            </label>
            <select
              id="site"
              value={form.siteId}
              onChange={(e) => {
                const site = selectedCustomer.sites.find((s) => s.id === e.target.value);
                setForm({ ...form, siteId: e.target.value, areaId: site?.areaId ?? form.areaId });
                if (site) setAreaQuery(site.areaName);
              }}
              className="input-field"
            >
              <option value="">Choose a site…</option>
              {selectedCustomer.sites.map((site) => (
                <option key={site.id} value={site.id}>
                  {site.label}
                </option>
              ))}
            </select>
          </div>
        )}

        <div>
          <label htmlFor="description" className="label">
            Notes
          </label>
          <textarea
            id="description"
            rows={3}
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            className="input-field"
            placeholder="Anything the technician should know before arriving."
          />
        </div>

        {create.isError && (
          <p role="alert" className="rounded-xl bg-danger-50 p-3 text-sm text-danger-600">
            {(create.error as Error).message}
          </p>
        )}

        {notice && (
          <p role="alert" className="rounded-xl bg-accent-50 p-3 text-sm text-accent-900">
            {notice}
          </p>
        )}

        {missing.length > 0 && !notice && (
          <p className="text-sm text-muted-foreground">Still needed: {missing.join(' and ')}.</p>
        )}

        <div className="flex flex-col gap-2 sm:flex-row">
          <button type="submit" disabled={create.isPending} className="btn-primary flex-1">
            {create.isPending && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
            Create ticket
          </button>

          {/*
            For a call where half the facts are missing — a name and a street,
            no account number. Before this the choice was to invent the rest or
            lose what you had, and the second one happens on a notebook.
          */}
          <button
            type="button"
            disabled={create.isPending}
            className="btn-secondary flex-1"
            onClick={() => tryToCreate(true)}
          >
            <FileText className="h-4 w-4" aria-hidden="true" />
            Save as draft
          </button>
        </div>
        <p className="text-center text-xs text-muted-foreground">
          A draft notifies nobody and does not start the SLA clock. Submit it when you have the
          rest.
        </p>
      </form>
    </div>
  );
}
